import { useMemo } from "react";
import * as Yup from "yup";
import { ProformaInvoiceFormFields } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";
import { LineSheetSetType } from "@/features/line-sheet-sets/line-sheet-set.types";
import { Company, StoredObject } from "@/features/types";
import { PaymentMethod } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import { LinkedOrderConfirmationInvoice } from "@/features/invoices/deposit-invoices/app-order-confirmation-invoices-exclusive-selector";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useIdentity from "@/features/ui/hooks/use-identity";

// 브랜드별 금액은 더 이상 입력하지 않는다(2026-10-30) — 총액·사용 디파짓만 검사한다. 브랜드 행은 서버가 채운다.
export default function useProformaInvoiceFormValidationSchema() {
  const { t } = useI18nHelper();
  const identity = useIdentity();
  const { withMessage } = useYupHelper({
    ns: "proforma_invoices",
    prefix: "upload_proforma_invoices_form",
  });

  return useMemo(() => {
    return Yup.object<ProformaInvoiceFormFields>({
      name: Yup.string(),
      season: Yup.string().required(withMessage.required),
      lineSheetSetType: Yup.mixed<LineSheetSetType>().when(
        ["orderSheetSets", "orderConfirmationInvoices", "name"],
        (values: any, schema) => {
          if (identity?.company?.type === "BOUTIQUE") {
            return schema;
          } else {
            const [orderSheetSets, orderConfirmationInvoices] = values as [
              ProformaInvoiceFormFields["orderSheetSets"],
              ProformaInvoiceFormFields["orderConfirmationInvoices"]
            ];
            if (
              orderSheetSets.length === 0 &&
              orderConfirmationInvoices.length === 0
            ) {
              return schema.required(withMessage.required);
            }
          }

          return schema;
        }
      ),
      orderedBy: Yup.mixed<Company>().required(withMessage.required),
      issuedBy: Yup.mixed<Company>().when({
        is: () => identity?.company?.type !== "BOUTIQUE",
        then: (schema) => schema.required(withMessage.required),
        otherwise: (schema) => schema,
      }),
      issuedThrough: Yup.mixed<Company>().when({
        is: () => identity?.company?.type !== "BOUTIQUE",
        then: (schema) => schema.required(withMessage.required),
        otherwise: (schema) => schema,
      }),
      issuedOn: Yup.date().required(withMessage.required),
      exposedToBuyer: Yup.boolean().required(withMessage.required),
      paidByAgency: Yup.boolean().required(withMessage.required),
      paymentMethod: Yup.mixed<PaymentMethod>().required(withMessage.required),
      unoFeeRate: Yup.number()
        .label("uno_fee_rate")
        .when({
          is: () => identity?.company?.type !== "BOUTIQUE",
          then: (schema) => schema.required(withMessage.required),
          otherwise: (schema) => schema,
        }),
      orderConfirmationInvoices: Yup.array()
        .of(
          Yup.object<LinkedOrderConfirmationInvoice>({
            id: Yup.number().positive().required(),
            name: Yup.string().required(),
          })
        )
        .test(
          "not_both_of_order_confirmation_invoice_and_order_sheet_set",
          t(
            "upload_proforma_invoices_form.messages.error.not_both_of_order_confirmation_invoice_and_order_sheet_set",
            { ns: "proforma_invoices" }
          ),
          (orderConfirmInvoices, context) => {
            const { orderSheetSets } = context.parent;

            return !(
              (orderConfirmInvoices || []).length > 0 &&
              (orderSheetSets || []).length > 0
            );
          }
        ),
      file: Yup.mixed<File | Pick<StoredObject, "id" | "name">>()
        .label("invoice_file")
        .required(withMessage.required),
      // 인보이스 총액 — 담당자가 인보이스 금액을 그대로 넣는다
      totalAmount: Yup.object({
        value: Yup.number()
          .label("total_amount")
          .min(0, withMessage.min)
          .when({
            is: () => identity?.company?.type !== "BOUTIQUE",
            then: (schema) => schema.required(withMessage.required),
            otherwise: (schema) => schema,
          }),
        currency: Yup.string(),
      })
        .label("total_amount")
        .when({
          is: () => identity?.company?.type !== "BOUTIQUE",
          then: (schema) => schema.required(withMessage.required),
          otherwise: (schema) => schema,
        }),
      // 사용 디파짓은 연결한 OC 의 예상 잔여 디파짓을 넘을 수 없다
      usedDeposit: Yup.object({
        value: Yup.number().min(0, withMessage.min),
        currency: Yup.string(),
      }).test(
        "exceeding_used_deposit",
        t(
          "upload_proforma_invoices_form.messages.error.exceeding_used_deposit",
          { ns: "proforma_invoices" }
        ),
        (value, context) => {
          const { expectedRemainingDepositAmount } =
            context.parent as ProformaInvoiceFormFields;
          const used = value?.value ?? 0;
          if (used <= 0) {
            return true;
          }
          return used <= (expectedRemainingDepositAmount?.value ?? 0) + 0.005;
        }
      ),
    });
  }, [identity?.company?.type, t, withMessage.min, withMessage.required]);
}
